import React from 'react';
import { CheckIcon } from 'lucide-react';
import { Assumptions, CompModel } from '../types/compensation';
import { formatDkk, formatPercent } from '../utils/format';
import { vestingSummary } from '../utils/compensation';
interface EquityPanelProps {
  model: CompModel;
  assumptions: Assumptions;
  onTogglePerformanceWarrants: (value: boolean) => void;
}
export function EquityPanel({
  model,
  assumptions,
  onTogglePerformanceWarrants
}: EquityPanelProps) {
  return <section aria-label="Warrants and ownership" className="rounded-card border border-line bg-ink-900 p-6 lg:p-7" data-id="element-64" data-mp="1otnqrsn3qtxz">
      <div className="flex items-center gap-2.5" data-id="element-65" data-mp="1b3255w69k5we">
        <span className="h-2 w-2 rounded-full bg-ownership" aria-hidden="true" data-id="element-66" data-mp="1b065a2auv75b" />
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ownership" data-id="element-67" data-mp="1fziu2slaeoaj">
          Ownership
        </h2>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-6" data-id="element-68" data-mp="28hedcfg3f9cw">
        <div data-id="element-69" data-mp="28j7kdvumbvlf">
          <p className="text-xs uppercase tracking-[0.14em] text-paper-dim" data-id="element-70" data-mp="28jr51kbnky35">
            Allocation value
          </p>
          <p className="tnum mt-1.5 text-2xl font-semibold text-paper" data-id="element-71" data-mp="2bhhhb5mkxqyj">
            {formatDkk(model.equityValue)}
          </p>
          <p className="tnum mt-1 text-xs text-paper-dim" data-id="element-72" data-mp="2p95s95l3e9dx">
            {formatDkk(model.equityCash)} × {assumptions.equityMultiplier}
          </p>
        </div>
        <div data-id="element-73" data-mp="2kgcnihpmj11u">
          <p className="text-xs uppercase tracking-[0.14em] text-paper-dim" data-id="element-74" data-mp="2z723ryhxz6ck">
            Indicative fully diluted
          </p>
          <p className="tnum mt-1.5 text-2xl font-semibold text-ownership" data-id="element-75" data-mp="2u8s87msb23qf">
            {formatPercent(model.ownershipPercent, 2)}
          </p>
          <p className="tnum mt-1 text-xs text-paper-dim" data-id="element-76" data-mp="385nqm5ug57j5">
            at {formatDkk(assumptions.companyValuation)} valuation
          </p>
        </div>
      </div>

      <p className="mt-5 rounded-lg border border-line bg-ink-950 p-4 text-xs leading-relaxed text-paper-muted" data-id="element-77" data-mp="1duigcioutwuu">
        Indicative option/warrant allocation — actual economic value depends on
        strike price, vesting, dilution, tax treatment and exit value.
      </p>

      <div className="mt-5 border-t border-line-soft pt-4" data-id="element-78" data-mp="3dvgoic1wn1pb">
        <p className="text-xs uppercase tracking-[0.14em] text-paper-dim" data-id="element-79" data-mp="3dy2xmg4g1p9n">
          Vesting
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-paper" data-id="element-80" data-mp="3ixfr3jptlul7">
          {vestingSummary(assumptions)}
        </p>
      </div>

      <div className="mt-5 border-t border-line-soft pt-4" data-id="element-81" data-mp="3rw9mx7vbugxp">
        <label className="flex cursor-pointer items-start gap-3" data-id="element-82" data-mp="3rtndt3ssftdd">
          <span className="relative mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border border-line bg-ink-950" data-id="element-83" data-mp="o5gs0vcr40h">
            <input type="checkbox" checked={assumptions.performanceWarrants} onChange={e => onTogglePerformanceWarrants(e.target.checked)} className="absolute inset-0 cursor-pointer opacity-0" data-id="element-84" data-mp="af9wj2suqswd" />
            {assumptions.performanceWarrants ? <CheckIcon className="h-3.5 w-3.5 text-ownership" aria-hidden="true" data-id="element-85" data-mp="23xqr8bqhj8ul" /> : null}
          </span>
          <span className="text-sm text-paper" data-id="element-86" data-mp="1z10bjpilef9p">
            Additional performance warrants for strategic account milestones
          </span>
        </label>

        {assumptions.performanceWarrants ? <ul className="mt-4 space-y-2 pl-[30px]" data-id="element-87" data-mp="1q0k82g1un84u">
            {assumptions.milestones.map(m => <li key={m.id} className="flex items-baseline justify-between gap-4 text-sm" data-id="element-88" data-mp="2mqb1ebyemp92">
                <span className="text-paper-muted" data-id="element-89" data-mp="2dvd5qx3vgye1">{m.label}</span>
                <span className="tnum shrink-0 text-paper-dim" data-id="element-90" data-mp="28x390i2i1pqb">
                  {m.grant.trim() === '' ? 'To be agreed' : m.grant}
                </span>
              </li>)}
          </ul> : null}
      </div>
    </section>;
}